import type { CSSObject } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  Spacing,
  Typeface,
  Typography,
} from '@opallabs/gem'
import type { StyleProps } from './types'

const labelFont = (size: number): CSSObject => ({
  fontSize: size * 0.4,
  fontFamily: Typeface.Default,
})

export const container = ({
  shouldDisplayRole,
  size,
  theme,
}: StyleProps): CSSObject => ({
  ...(shouldDisplayRole && {
    gap: Spacing.S,
    paddingBlock: size * 0.025,
    paddingInlineEnd: size * 0.08,
    paddingInlineStart: size / 3,
  }),
  alignItems: 'center',
  backgroundColor: Color[theme].Surface.N100,
  border: `1px solid ${Color[theme].Surface.N600}`,
  color: Color[theme].Text.Default,
  borderRadius: size,
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'nowrap',
  fontFamily: Typeface.Default,
  justifyContent: 'space-between',
  lineHeight: 1,
  position: 'relative',

  '.kuiper-popover-base': {
    // fix vertical placement of role label at small sizes
    display: 'flex',
  },
  '.kuiper-popover-base > span': {
    marginInlineEnd: size / 5,
    textTransform: 'uppercase',
  },
})

export const avatarContainer = ({
  clickable,
  size,
  shouldDisplayRole,
  theme,
}: StyleProps & { clickable: boolean }): CSSObject => ({
  ...(clickable && { cursor: 'pointer' }),
  ...labelFont(size),
  alignItems: 'center',
  backgroundColor: Color[theme].Surface.N200,
  color: Color[theme].Text.Default,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',

  // compensate avatar size for role badge to get consistent total component height
  blockSize: shouldDisplayRole ? size - 6 : size - 2,
  inlineSize: shouldDisplayRole ? size - 6 : size - 2,

  '&:focus': {
    ...FocusIndicator[theme],
  },
})

export const badge = ({ shouldDisplayRole, size }: StyleProps): CSSObject => ({
  backgroundColor: '#FAB14D',
  borderRadius: '50%',
  insetBlockEnd: shouldDisplayRole ? Spacing.XS : 0,
  blockSize: size / Spacing.XS,
  position: 'absolute',
  insetInlineEnd: shouldDisplayRole ? Spacing.XS : 0,
  inlineSize: size / Spacing.XS,
})

export const roleLabel = ({ size }: Pick<StyleProps, 'size'>): CSSObject => ({
  ...labelFont(size),
})

export const rolePopover = ({
  theme,
}: Pick<StyleProps, 'theme'>): CSSObject => ({
  paddingBlock: Spacing.XS * 2.5,
  paddingInline: Spacing.XS * 3,
  textAlign: 'center',
  color: Color[theme].Text.Default,
  ...Typography.T500,

  '& > div:last-of-type': {
    textTransform: 'none',
    color: Color[theme].Text.Gray,
  },
})
